import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { AppointmentService } from '@avanta/shared/services/appointments';
import { type Appointment } from '@avanta/shared/types/appointments';
import { useMutation, type UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { type AxiosResponse, type AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { MUTATION_KEYS, QUERY_KEYS } from 'constants/common';
import { ROUTES } from 'constants/routes';
import { type EditAppointmentVariables } from 'pages/AppointmentsReserve/types';
import { useUploadDocument } from './useUploadDocument';

export const useEditAppointment = (): UseMutationResult<
  AxiosResponse<Appointment>,
  AxiosError,
  EditAppointmentVariables
> => {
  const queryClient = useQueryClient();
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);
  const { mutate: uploadDocument } = useUploadDocument();
  const navigate = useNavigate();

  return useMutation<AxiosResponse<Appointment>, AxiosError, EditAppointmentVariables>({
    mutationKey: [MUTATION_KEYS.UPDATE_APPOINTMENT],
    mutationFn: async ({ payload }) => AppointmentService.edit(payload),
    onSuccess: async ({ data }, { document }) => {
      if (document && data) {
        uploadDocument({ appointmentId: data.id, document });
      }
      toast.success(t('EditAppointment.success'));
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FETCH_ENTERPRISE_APPOINTMENTS] });
      navigate(ROUTES.APPOINTMENTS);
    },
    onError: error => {
      //TODO usar custom error code
      if (error?.status === 403) {
        toast.error(t('EditAppointment.error.forbidden'), {
          id: 'edit-appointment-error-forbidden',
        });
      } else {
        toast.error(t('EditAppointment.error.generic'), { id: 'edit-appointment-error-generic' });
      }
    },
  });
};
